// MF-568 — The school's own PAT settings. Maestro ships no norm: every PAT chart that needs one (stanines, the score
// benchmark of the growth chart) shows "not configured" until the school enters it here. Curators only.
// Tests, year levels and skill areas are CHOSEN from what the school's own PAT data contains (so they always match);
// numbers are typed digit by digit from the right (like a till), so a comma or a dot can never be wrong.
import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faChevronDown,
  faCircleCheck,
  faCircleInfo,
  faLayerGroup,
  faNoteSticky,
  faPlus,
  faRulerCombined,
  faSliders,
  faTrash,
  faTriangleExclamation
} from '@fortawesome/pro-light-svg-icons'
import { useSnackbar } from 'notistack'
import {
  getLibrarySettings,
  getParamValues,
  saveLibrarySettings
} from '../../../../../services/boards/boards.service'
import { SUMMARY_PALETTE } from '../boardTheme'

const CUTS = 8
const EMPTY_CUTS = Array(CUTS).fill('')
const newNorm = (scaleSlug = '') => ({ scaleSlug, yearLevel: '', mean: '', sd: '', cuts: [...EMPTY_CUTS] })

const yearNumber = (y) => parseInt(String(y).replace(/\D/g, ''), 10)
// The same year can be stored as "Year 6" and as "6": offer it once, as "Year 6" when that spelling exists
const yearKey = (y) => {
  const m = /^(?:year|yr|y)?\s*(\d{1,2})$/i.exec(String(y).trim())
  return m ? `y${Number(m[1])}` : String(y).trim().toLowerCase()
}
const oneSpellingPerYear = (list) => {
  const best = new Map()
  for (const y of list) {
    const k = yearKey(y)
    if (!best.has(k) || (/^year/i.test(y) && !/^year/i.test(best.get(k)))) best.set(k, y)
  }
  return [...best.values()]
}
const byYear = (a, b) =>
  (Number.isNaN(yearNumber(a)) ? 99 : yearNumber(a)) - (Number.isNaN(yearNumber(b)) ? 99 : yearNumber(b)) ||
  a.localeCompare(b)

/** A number typed digit by digit from the right: 1 2 9 4 → 129.4 (one decimal). Paste a number and it is read as written. */
const DecimalField = ({ value, onChange, decimals = 1, max = 99999, sx, ...rest }) => {
  const shown = value === '' || value === null || value === undefined ? '' : Number(value).toFixed(decimals)
  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 7)
    // all zeros (or nothing left after backspacing) means empty, so the default applies and the field can be cleared
    if (!digits || /^0+$/.test(digits)) return onChange('')
    onChange(Math.min(max, Number(digits) / 10 ** decimals))
  }
  const paste = (e) => {
    const n = Number(String(e.clipboardData.getData('text')).trim().replace(',', '.'))
    if (!Number.isFinite(n)) return
    e.preventDefault()
    onChange(Math.min(max, Math.max(0, Math.round(n * 10 ** decimals) / 10 ** decimals)))
  }
  return (
    <TextField
      size='small'
      value={shown}
      onChange={handle}
      onPaste={paste}
      inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
      sx={sx}
      {...rest}
    />
  )
}

/** A choice among the values in the school's data; when the data has none yet, anything may be typed. */
const ChoiceField = ({ options, value, onChange, label, width, tip }) => (
  <Autocomplete
    size='small'
    freeSolo={options.length === 0}
    options={options}
    value={value || null}
    onChange={(_, v) => onChange(v || '')}
    onInputChange={(_, v, reason) => options.length === 0 && reason === 'input' && onChange(v)}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        InputProps={
          tip
            ? {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <HelpTip>{tip}</HelpTip>
                  </>
                )
              }
            : params.InputProps
        }
      />
    )}
    sx={{ width }}
  />
)

/** A small (i) that explains a value in plain words, on hover, focus or tap. */
const HelpTip = ({ children }) => (
  <Tooltip
    arrow
    enterTouchDelay={0}
    title={<Box sx={{ fontSize: 12, lineHeight: 1.5, maxWidth: 320 }}>{children}</Box>}
  >
    <Box
      component='span'
      tabIndex={0}
      aria-label='What is this?'
      sx={{ ml: 0.75, color: SUMMARY_PALETTE.muted, cursor: 'help', display: 'inline-flex' }}
    >
      <FontAwesomeIcon icon={faCircleInfo} />
    </Box>
  </Tooltip>
)
const tipAdornment = (text) => ({
  endAdornment: (
    <InputAdornment position='end'>
      <HelpTip>{text}</HelpTip>
    </InputAdornment>
  )
})

/** Several values chosen from the school's data (chips); when the data has none yet, anything may be typed. */
const ChoiceMulti = ({ options, value, onChange, label, width, placeholder }) => (
  <Autocomplete
    multiple
    size='small'
    freeSolo={options.length === 0}
    options={options}
    value={value}
    onChange={(_, v) => onChange(v)}
    renderTags={(tags, getTagProps) =>
      tags.map((t, i) => <Chip size='small' label={t} {...getTagProps({ index: i })} key={t} />)
    }
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder={value.length ? '' : placeholder} />
    )}
    sx={{ width, minWidth: 220 }}
  />
)

const GLOSSARY = [
  [
    'PAT result',
    'Each PAT test gives a student a scale score, for example 129.4. A band is a 10-point range of scores (125-134).'
  ],
  [
    'Stanine',
    'A number from 1 to 9 that says how a score compares with other students of the same year level: 5 is average, 1 to 3 are below, 7 to 9 are above. It is not growth: a stanine can fall while the score rises.'
  ],
  [
    'Norm (mean and SD)',
    'The reference group a stanine and the score benchmark come from. The mean is its average score and the SD (standard deviation) says how spread out the scores are. ACER publishes them for each test and year level; type the ones your school uses. Maestro does not ship any, so until you do the charts say “not configured”.'
  ],
  [
    'Growth',
    'How many points a student gained between two results, divided by how spread out the class is (the pooled SD). The growth threshold is where “high growth” starts.'
  ],
  [
    'Near-level window',
    'How close a question must be to a score to count as near that level: with 10, a student on 120 is matched with questions of difficulty 110 to 130.'
  ],
  [
    'Groups',
    'A name for a set of year levels and/or classes (for example Primary, or 6A + 6B). A tile can then show that group by choosing its name in the Group option.'
  ]
]

/** A settings block: icon, title, one line of what it is for, and its content on a white card. */
const SettingsCard = ({ icon, title, subtitle, help, action, children }) => (
  <Paper
    variant='outlined'
    sx={{ borderRadius: '14px', p: 2, mt: 2, bgcolor: '#fff', borderColor: SUMMARY_PALETTE.grid }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '11px',
          bgcolor: 'rgba(47,102,144,.1)',
          color: 'primary.main',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: 700,
            color: SUMMARY_PALETTE.ink,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {title}
          {help ? <HelpTip>{help}</HelpTip> : null}
        </Typography>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
)

/** Nothing here yet: a calm dashed box with what it will do and a button to start. */
const EmptyCard = ({ text, children }) => (
  <Box
    sx={{
      border: `1px dashed ${SUMMARY_PALETTE.grid}`,
      borderRadius: '12px',
      p: 2,
      textAlign: 'center',
      bgcolor: '#fafbfd'
    }}
  >
    <Typography sx={{ fontSize: 12.5, color: SUMMARY_PALETTE.muted, mb: children ? 1 : 0 }}>
      {text}
    </Typography>
    {children}
  </Box>
)

const SOFT = { bgcolor: '#f6f9fc', borderRadius: '12px', border: '1px solid #e6edf4' }

// "pat-maths-scale" → "Maths": what the school calls the test, for the chip on a norm
const prettyTest = (slug) =>
  String(slug || 'Test')
    .replace(/^pat-/, '')
    .replace(/-scale$/, '')
    .replace(/^./, (c) => c.toUpperCase())

const toForm = (pat = {}) => ({
  growthThreshold: pat.growthThreshold ?? '',
  itemWindow: pat.itemWindow ?? '',
  nextLearningGroups: pat.nextLearningGroups ?? '',
  norms: (pat.norms || []).map((n) => ({
    scaleSlug: n.scaleSlug,
    yearLevel: n.yearLevel,
    mean: n.mean ?? '',
    sd: n.sd ?? '',
    cuts: Array.from({ length: CUTS }, (_, i) => n.stanineCuts?.[i] ?? '')
  })),
  notes: (pat.teachingNotes || []).map((n) => ({ strand: n.strand, text: n.text })),
  groups: (pat.groups || []).map((g) => ({
    name: g.name,
    yearLevels: g.yearLevels || [],
    classes: g.classes || []
  }))
})

/** What is wrong, in plain words, before anything is sent. */
const problems = (f) => {
  const out = []
  if (f.growthThreshold !== '' && (f.growthThreshold < 0 || f.growthThreshold > 3))
    out.push('The growth threshold must be between 0 and 3.')
  if (f.itemWindow !== '' && (f.itemWindow < 1 || f.itemWindow > 50))
    out.push('The near-level window must be between 1 and 50 points.')
  if (f.nextLearningGroups !== '' && (f.nextLearningGroups < 2 || f.nextLearningGroups > 6))
    out.push('The number of ability groups must be between 2 and 6.')
  const seen = new Set()
  f.norms.forEach((n, i) => {
    const name = `Norm ${i + 1}`
    if (!n.scaleSlug || !n.yearLevel) out.push(`${name}: choose the test and the year level.`)
    if (n.mean === '') out.push(`${name}: the mean is needed (it is the score benchmark).`)
    if (n.sd !== '' && n.sd <= 0) out.push(`${name}: the standard deviation must be above 0.`)
    const key = `${n.scaleSlug}|${yearKey(n.yearLevel)}`.toLowerCase()
    if (n.scaleSlug && n.yearLevel && seen.has(key))
      out.push(`${name}: ${n.scaleSlug} ${n.yearLevel} is already in the list.`)
    seen.add(key)
    const filled = n.cuts.filter((c) => c !== '')
    if (filled.length && filled.length < CUTS) out.push(`${name}: type all 8 stanine cut scores, or none.`)
    if (filled.length === CUTS && !n.cuts.every((c, j) => j === 0 || c > n.cuts[j - 1]))
      out.push(`${name}: the stanine cut scores must go up, each higher than the one before.`)
  })
  const seenGroups = new Set()
  f.groups.forEach((g, i) => {
    const name = g.name.trim()
    if (!/^[\w .,'&()/+-]{1,64}$/.test(name))
      out.push(`Group ${i + 1}: give it a name (letters, numbers, spaces and . , ' & ( ) / + -).`)
    if (seenGroups.has(name.toLowerCase())) out.push(`Group ${i + 1}: the name "${name}" is already used.`)
    seenGroups.add(name.toLowerCase())
    if (!g.yearLevels.length && !g.classes.length)
      out.push(`Group ${i + 1}: choose at least one year level or class.`)
  })
  f.notes.forEach((n, i) => {
    if ((n.strand && !n.text.trim()) || (!n.strand && n.text.trim()))
      out.push(`Note ${i + 1}: choose the skill area and write the approach.`)
  })
  return out
}

const toPayload = (f) => ({
  ...(f.growthThreshold !== '' ? { growthThreshold: f.growthThreshold } : {}),
  ...(f.itemWindow !== '' ? { itemWindow: f.itemWindow } : {}),
  ...(f.nextLearningGroups !== '' ? { nextLearningGroups: f.nextLearningGroups } : {}),
  norms: f.norms.map((n) => ({
    scaleSlug: n.scaleSlug,
    yearLevel: n.yearLevel,
    mean: n.mean,
    ...(n.sd !== '' ? { sd: n.sd } : {}),
    ...(n.cuts.every((c) => c !== '') ? { stanineCuts: n.cuts } : {})
  })),
  teachingNotes: f.notes
    .filter((n) => n.strand && n.text.trim())
    .map((n) => ({ strand: n.strand, text: n.text.trim() })),
  groups: f.groups.map((g) => ({ name: g.name.trim(), yearLevels: g.yearLevels, classes: g.classes }))
})

const PatSettingsDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [form, setForm] = useState(null)
  const [options, setOptions] = useState({ scales: [], years: [], strands: [], classes: [] })
  const [errors, setErrors] = useState([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(null)
    setErrors([])
    const values = (code, key) =>
      getParamValues(code, key)
        .then((l) => (Array.isArray(l) ? l.map(String) : []))
        .catch(() => [])
    Promise.all([
      getLibrarySettings().then((s) => toForm(s?.pat)),
      values('pat-class-ranking', 'scaleSlug'),
      values('pat-class-ranking', 'yearLevel'),
      values('pat-next-learning', 'strand'),
      values('pat-class-ranking', 'className')
    ])
      .then(([f, scales, years, strands, classes]) => {
        setOptions({
          scales,
          years: oneSpellingPerYear(years).sort(byYear),
          strands,
          classes: classes.filter(Boolean)
        })
        setForm(f)
      })
      .catch((err) => setErrors([err?.message || 'The settings could not be loaded']))
  }, [open])

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const setRow = (key, i, patch) =>
    setForm((f) => ({ ...f, [key]: f[key].map((r, j) => (j === i ? { ...r, ...patch } : r)) }))
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...f[key], row] }))
  const dropRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }))

  const save = async () => {
    const found = problems(form)
    setErrors(found)
    if (found.length) return
    setBusy(true)
    try {
      await saveLibrarySettings(toPayload(form))
      enqueueSnackbar('PAT settings saved', { variant: 'success' })
      onClose()
    } catch (err) {
      setErrors([err?.message || 'The settings could not be saved'])
    } finally {
      setBusy(false)
    }
  }

  const noData = options.scales.length === 0
  const counts = form
    ? { norms: form.norms.length, groups: form.groups.length, notes: form.notes.length }
    : null
  const addButton = (label, onClick) => (
    <Button
      size='small'
      variant='outlined'
      startIcon={<FontAwesomeIcon icon={faPlus} />}
      onClick={onClick}
      sx={{ flexShrink: 0 }}
    >
      {label}
    </Button>
  )
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', bgcolor: '#f3f6fa' } }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: '#fff',
          px: 3,
          py: 2.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.75
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '13px',
            bgcolor: 'rgba(255,255,255,.18)',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <FontAwesomeIcon icon={faChartLine} size='lg' />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>PAT settings</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: 0.9 }}>
            Your school&apos;s own reference values for the PAT charts. Maestro does not ship any.
          </Typography>
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {counts ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              size='small'
              icon={
                <FontAwesomeIcon
                  icon={counts.norms ? faCircleCheck : faTriangleExclamation}
                  style={{ width: 13, height: 13 }}
                />
              }
              sx={{ '& .MuiChip-icon': { fontSize: 13, ml: 1, mr: -0.25 } }}
              color={counts.norms ? 'success' : 'warning'}
              variant='outlined'
              label={
                counts.norms
                  ? `${counts.norms} norm${counts.norms === 1 ? '' : 's'}`
                  : 'No norms yet: stanines and the score benchmark show “not configured”'
              }
            />
            <Chip
              size='small'
              variant='outlined'
              label={`${counts.groups} group${counts.groups === 1 ? '' : 's'}`}
            />
            <Chip
              size='small'
              variant='outlined'
              label={`${counts.notes} teaching note${counts.notes === 1 ? '' : 's'}`}
            />
          </Box>
        ) : null}
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 1.5,
            borderRadius: '14px !important',
            border: `1px solid ${SUMMARY_PALETTE.grid}`,
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>What do these settings mean?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {GLOSSARY.map(([term, text]) => (
              <Typography
                key={term}
                sx={{ fontSize: 12.5, color: SUMMARY_PALETTE.muted, mb: 1, lineHeight: 1.55 }}
              >
                <b style={{ color: SUMMARY_PALETTE.ink }}>{term}.</b> {text}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
        {errors.length ? (
          <Alert severity='error' sx={{ mt: 2, borderRadius: '12px' }}>
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </Alert>
        ) : null}
        {!form ? (
          <Skeleton variant='rounded' height={260} sx={{ mt: 2, borderRadius: '14px' }} />
        ) : (
          <>
            <SettingsCard
              icon={faSliders}
              title='Charts'
              subtitle='How the PAT charts read the results. Leave a box empty to keep its default.'
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))'
                }}
              >
                <Box sx={{ ...SOFT, p: 1.5 }}>
                  <DecimalField
                    fullWidth
                    label='Growth threshold'
                    decimals={2}
                    max={3}
                    value={form.growthThreshold}
                    onChange={(v) => set('growthThreshold', v)}
                    placeholder='0.26'
                    helperText='In SD units: where “high growth” starts (0 to 3). Default 0.26'
                    InputProps={tipAdornment(
                      'Growth is the points a student gained divided by how spread out the class is (the standard deviation). From this value up it counts as high growth. Example: if the class spread is 8 points, 0.26 means about 2 points. Higher is stricter. Empty uses 0.26.'
                    )}
                  />
                </Box>
                <Box sx={{ ...SOFT, p: 1.5 }}>
                  <DecimalField
                    fullWidth
                    label='Near-level window'
                    decimals={0}
                    max={50}
                    value={form.itemWindow}
                    onChange={(v) => set('itemWindow', v)}
                    placeholder='10'
                    helperText='± points: how close a question must be to a score (1 to 50). Default 10'
                    InputProps={tipAdornment(
                      'A question is near a level when its difficulty is within this many points of the score. With 10, a student on 120 is matched with questions of difficulty 110 to 130. Used by the response matrix and "what to teach next". Empty uses 10.'
                    )}
                  />
                </Box>
                <Box sx={{ ...SOFT, p: 1.5 }}>
                  <DecimalField
                    fullWidth
                    label='Ability groups'
                    decimals={0}
                    max={6}
                    value={form.nextLearningGroups}
                    onChange={(v) => set('nextLearningGroups', v)}
                    placeholder='3'
                    helperText='Groups in “what to teach next” (2 to 6). Default 3'
                    InputProps={tipAdornment(
                      'How many groups "what each group is ready to learn" splits a class into, from the lowest scores to the highest. Empty uses 3.'
                    )}
                  />
                </Box>
              </Box>
            </SettingsCard>

            <SettingsCard
              icon={faRulerCombined}
              title='Norms by test and year level'
              subtitle='The reference group behind stanines and the score benchmark.'
              help='A norm is the reference group your stanines and the score benchmark come from. Add one per test and year level; without it those parts of the charts show "not configured".'
              action={addButton('Add a norm', () => addRow('norms', newNorm(options.scales[0] || '')))}
            >
              {noData ? (
                <Alert severity='info' sx={{ mb: 1.5, borderRadius: '12px' }}>
                  This school has no PAT results loaded yet, so the tests and year levels cannot be listed:
                  type them exactly as the school writes them.
                </Alert>
              ) : null}
              {form.norms.length === 0 ? (
                <EmptyCard text='No norms yet. Add the mean (and SD) ACER gives for a test and year level to get stanines and the benchmark line.' />
              ) : null}
              {form.norms.map((n, i) => (
                <Box key={i} sx={{ ...SOFT, p: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
                    <Chip size='small' color='primary' label={prettyTest(n.scaleSlug)} />
                    {n.yearLevel ? <Chip size='small' variant='outlined' label={n.yearLevel} /> : null}
                    {n.mean !== '' ? (
                      <Chip size='small' variant='outlined' label={`Mean ${Number(n.mean).toFixed(1)}`} />
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    <IconButton size='small' aria-label='Remove norm' onClick={() => dropRow('norms', i)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <ChoiceField
                      tip='The PAT test this norm is for, as it appears in your results.'
                      label='Test'
                      options={options.scales}
                      value={n.scaleSlug}
                      onChange={(v) => setRow('norms', i, { scaleSlug: v })}
                      width={270}
                    />
                    <ChoiceField
                      tip={'The year level the norm applies to. "Year 6" also covers students stored as "6".'}
                      label='Year level'
                      options={options.years}
                      value={n.yearLevel}
                      onChange={(v) => setRow('norms', i, { yearLevel: v })}
                      width={180}
                    />
                    <DecimalField
                      label='Mean'
                      InputProps={tipAdornment(
                        'The average score of the reference group for this test and year level (ACER publishes it in its norms tables). It is the benchmark line of "Growth and achievement" and the centre of the stanine scale.'
                      )}
                      decimals={1}
                      value={n.mean}
                      onChange={(v) => setRow('norms', i, { mean: v })}
                      sx={{ width: 130 }}
                    />
                    <DecimalField
                      label='SD (optional)'
                      InputProps={tipAdornment(
                        'The standard deviation: how spread out the reference scores are. With the mean it lets Maestro work out stanines. Optional if you type the 8 cut scores below.'
                      )}
                      decimals={1}
                      value={n.sd}
                      onChange={(v) => setRow('norms', i, { sd: v })}
                      sx={{ width: 140 }}
                    />
                  </Box>
                  <Accordion
                    disableGutters
                    elevation={0}
                    defaultExpanded={n.cuts.some((c) => c !== '')}
                    sx={{ mt: 1.25, bgcolor: 'transparent', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                      sx={{ px: 0, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}>
                        Stanine cut scores (optional)
                        {n.cuts.every((c) => c !== '') ? ' · set' : ' · worked out from the mean and SD'}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.muted, mb: 0.75 }}>
                        The lowest score of each stanine, S2 to S9. Type all 8 or none.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {n.cuts.map((c, j) => (
                          <DecimalField
                            key={j}
                            label={`S${j + 2}`}
                            decimals={1}
                            value={c}
                            onChange={(v) =>
                              setRow('norms', i, { cuts: n.cuts.map((x, k) => (k === j ? v : x)) })
                            }
                            sx={{ width: 84 }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              ))}
            </SettingsCard>

            <SettingsCard
              icon={faLayerGroup}
              title='Groups of year levels and classes'
              subtitle='Optional. Name a set (Primary, 6A + 6B) and pick it in a tile’s Group option.'
              help='Give a set of year levels and/or classes one name, for example Primary (Prep to Year 6) or 6A + 6B. In a tile, choose that name in the Group option instead of one year level. A student is in the group when their year level is in the list and, if you also list classes, their class is too.'
              action={addButton('Add a group', () =>
                addRow('groups', { name: '', yearLevels: [], classes: [] })
              )}
            >
              {form.groups.length === 0 ? (
                <EmptyCard text='No groups yet. Handy to show several year levels or classes in one tile.' />
              ) : null}
              {form.groups.map((g, i) => (
                <Box
                  key={i}
                  sx={{ ...SOFT, p: 1.5, mb: 1.5, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                      size='small'
                      label='Group name'
                      value={g.name}
                      onChange={(e) => setRow('groups', i, { name: e.target.value })}
                      placeholder='Primary'
                      inputProps={{ maxLength: 64 }}
                      sx={{ width: 170 }}
                    />
                    <ChoiceMulti
                      label='Year levels'
                      placeholder='Choose…'
                      options={options.years}
                      value={g.yearLevels}
                      onChange={(v) => setRow('groups', i, { yearLevels: v })}
                      width={270}
                    />
                    <ChoiceMulti
                      label='Classes (optional)'
                      placeholder='Choose…'
                      options={options.classes}
                      value={g.classes}
                      onChange={(v) => setRow('groups', i, { classes: v })}
                      width={210}
                    />
                    <IconButton size='small' aria-label='Remove group' onClick={() => dropRow('groups', i)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </SettingsCard>

            <SettingsCard
              icon={faNoteSticky}
              title='Teaching notes by skill area'
              subtitle='Shown as the shared teaching approach in “what to teach next”.'
              help='A sentence your school writes for a skill area (for example Fractions). It shows as the shared teaching approach in "what to teach next". Maestro never writes these for you.'
              action={addButton('Add a note', () => addRow('notes', { strand: '', text: '' }))}
            >
              {form.notes.length === 0 ? <EmptyCard text='No notes yet. Leave empty to show none.' /> : null}
              {form.notes.map((n, i) => (
                <Box
                  key={i}
                  sx={{
                    ...SOFT,
                    p: 1.5,
                    mb: 1.5,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap'
                  }}
                >
                  <ChoiceField
                    label='Skill area'
                    options={options.strands}
                    value={n.strand}
                    onChange={(v) => setRow('notes', i, { strand: v })}
                    width={230}
                  />
                  <TextField
                    size='small'
                    label='Teaching approach'
                    value={n.text}
                    onChange={(e) => setRow('notes', i, { text: e.target.value })}
                    inputProps={{ maxLength: 400 }}
                    sx={{ flex: 1, minWidth: 240 }}
                  />
                  <IconButton size='small' aria-label='Remove note' onClick={() => dropRow('notes', i)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                </Box>
              ))}
            </SettingsCard>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 1.75, bgcolor: '#fff', borderTop: `1px solid ${SUMMARY_PALETTE.grid}` }}
      >
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant='contained' onClick={save} disabled={busy || !form}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PatSettingsDialog
